import React, { useEffect, useRef, useState } from "react";
import { addDays, format, getDay, isSameDay, subDays } from "date-fns";

import { CalendarDay } from "~/shared.types";
import { icons } from "~/ui";
import { tw } from "~/utils";

import "./DaySelector.css";

import {
  calendarDayFromDate,
  getAdjacentMonth,
  getCalendarDayAriaLabel,
  getSelectedDayMeta,
  isLastDayOfMonthVisible,
} from "../utils";

type DaySelectorProps = {
  days: CalendarDay[];
  selectedDay: CalendarDay;
  onDaySelect: (day: CalendarDay) => void;
  hasAppointments: (date: Date) => boolean;
  className?: string;
  onMonthChange: (monthYear: { month: string; year: number }) => void;
  currentMonthYear: { month: string; year: number };
  maxAllowedDate: Date;
  hideSelectedDay?: boolean;
};

const SCROLL_DIRECTION = {
  LEFT: "left",
  RIGHT: "right",
};

type ScrollDirection = (typeof SCROLL_DIRECTION)[keyof typeof SCROLL_DIRECTION];

const toPaddingDay = (date: Date): CalendarDay => ({
  ...calendarDayFromDate(date),
  dayOfWeek: format(date, "EEE").toUpperCase(),
  isToday: false,
  isTomorrow: false,
  isAvailable: false,
});

export const DaySelector = ({
  days,
  selectedDay,
  onDaySelect,
  hasAppointments,
  className,
  onMonthChange,
  currentMonthYear,
  maxAllowedDate,
  hideSelectedDay = false,
}: DaySelectorProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const lastMonthRef = useRef<string>("");
  const shouldScrollToLastWeek = useRef(false);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const leadingDayCount = days.length > 0 ? (getDay(days[0].date) + 6) % 7 : 0;
  const leadingDays = Array.from({ length: leadingDayCount }, (_, index) =>
    toPaddingDay(subDays(days[0].date, leadingDayCount - index)),
  );
  const trailingDayCount = (7 - ((leadingDayCount + days.length) % 7)) % 7;
  const trailingDays = Array.from({ length: trailingDayCount }, (_, index) =>
    toPaddingDay(addDays(days[days.length - 1].date, index + 1)),
  );

  const updateScrollButtons = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;

    // Check if first day of current month is visible
    const firstDay = days[0];
    const firstDayElement = container.querySelector(
      `[data-date="${firstDay.date.toISOString()}"]`,
    ) as HTMLElement;
    const isFirstDayVisible =
      firstDayElement &&
      firstDayElement.offsetLeft >= scrollLeft &&
      firstDayElement.offsetLeft < scrollLeft + clientWidth;

    // Check if we can scroll to previous month
    const today = new Date();
    const currentMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const previousMonth = new Date(
      firstDay.date.getFullYear(),
      firstDay.date.getMonth() - 1,
      1,
    );
    const canScrollToPreviousMonth =
      isFirstDayVisible && previousMonth >= currentMonth;

    setCanScrollLeft(scrollLeft > 0 || canScrollToPreviousMonth);

    const currentDate = new Date(
      `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
    );
    const nextMonthDate = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth() + 1,
      1,
    );
    const hasNextMonth = nextMonthDate <= maxAllowedDate;
    setCanScrollRight(
      scrollLeft < scrollWidth - clientWidth - 1 || hasNextMonth,
    );
  };

  const isManualMonthChange = useRef(false);

  // Detect visible month based on leftmost visible day
  const detectVisibleMonth = () => {
    if (isManualMonthChange.current) return;
    const container = scrollContainerRef.current;
    if (!container) return;

    const monthDayCells = (
      Array.from(container.children) as HTMLElement[]
    ).slice(leadingDayCount, leadingDayCount + days.length);
    const scrollLeft = container.scrollLeft;
    const clientWidth = container.clientWidth;

    const firstVisibleChild = monthDayCells.find((child) => {
      const childLeft = child.offsetLeft;
      const childRight = childLeft + child.offsetWidth;
      return childRight > scrollLeft;
    });

    const lastVisibleChild = [...monthDayCells].reverse().find((child) => {
      const childLeft = child.offsetLeft;
      return childLeft < scrollLeft + clientWidth;
    });

    if (!firstVisibleChild || !lastVisibleChild) return;

    const firstDateStr = firstVisibleChild.getAttribute("data-date");
    const lastDateStr = lastVisibleChild.getAttribute("data-date");
    if (!firstDateStr || !lastDateStr) return;

    const firstDate = new Date(firstDateStr);
    const lastDate = new Date(lastDateStr);

    const nearStart = scrollLeft <= 5;
    const nearEnd = scrollLeft + clientWidth >= container.scrollWidth - 5;

    let newMonth: string | undefined;
    let newYear: number | undefined;

    if (nearStart) {
      // Only trigger if first visible day is in previous month
      const currentMonthIndex = new Date(
        `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
      ).getMonth();
      const currentYear = currentMonthYear.year;
      const firstMonthIndex = firstDate.getMonth();
      const firstYear = firstDate.getFullYear();

      const isPrevMonth =
        (firstYear === currentYear &&
          firstMonthIndex === currentMonthIndex - 1) ||
        (firstYear === currentYear - 1 &&
          currentMonthIndex === 0 &&
          firstMonthIndex === 11);

      if (isPrevMonth) {
        newMonth = firstDate.toLocaleString("en-US", { month: "long" });
        newYear = firstDate.getFullYear();
      } else {
        return;
      }
    } else if (nearEnd) {
      // Only trigger if both first and last visible days are in next month
      const currentMonthIndex = new Date(
        `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
      ).getMonth();
      const currentYear = currentMonthYear.year;
      const lastMonthIndex = lastDate.getMonth();
      const lastYear = lastDate.getFullYear();
      const firstMonthIndex = firstDate.getMonth();
      const firstYear = firstDate.getFullYear();

      const isLastDateNextMonth =
        (lastYear === currentYear &&
          lastMonthIndex === currentMonthIndex + 1) ||
        (lastYear === currentYear + 1 &&
          currentMonthIndex === 11 &&
          lastMonthIndex === 0);

      const isFirstDateNextMonth =
        (firstYear === currentYear &&
          firstMonthIndex === currentMonthIndex + 1) ||
        (firstYear === currentYear + 1 &&
          currentMonthIndex === 11 &&
          firstMonthIndex === 0);

      // Only trigger if both first and last visible days are in the next month
      // This ensures the user has scrolled enough that the current month is no longer the primary view
      if (isLastDateNextMonth && isFirstDateNextMonth) {
        newMonth = lastDate.toLocaleString("en-US", { month: "long" });
        newYear = lastDate.getFullYear();
      } else {
        return;
      }
    } else {
      return;
    }

    const currentKey = `${newYear}-${newMonth}`;
    if (
      newMonth &&
      newYear &&
      lastMonthRef.current !== currentKey &&
      !(
        newMonth === currentMonthYear.month && newYear === currentMonthYear.year
      )
    ) {
      lastMonthRef.current = currentKey;
      const newDate = new Date(`${newMonth} 1, ${newYear}`);
      if (newDate <= maxAllowedDate) {
        onMonthChange({ month: newMonth, year: newYear });
      }
    }
  };

  const checkMonthScroll = () => {
    detectVisibleMonth();
    updateScrollButtons();
  };

  const triggerMonthChange = (targetDate: Date) => {
    if (targetDate <= maxAllowedDate) {
      isManualMonthChange.current = true;
      onMonthChange({
        month: targetDate.toLocaleString("en-US", { month: "long" }),
        year: targetDate.getFullYear(),
      });
      setTimeout(() => {
        isManualMonthChange.current = false;
      }, 500);
    }
  };

  const scrollByWeek = (direction: ScrollDirection) => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const firstChild = container.querySelector("div > div");
    if (!firstChild) return;

    const dayWidth = firstChild.getBoundingClientRect().width;

    if (direction === SCROLL_DIRECTION.LEFT) {
      const { scrollLeft } = container;
      if (scrollLeft <= 0) {
        const previousMonthDate = getAdjacentMonth(currentMonthYear, -1);

        shouldScrollToLastWeek.current = true;
        triggerMonthChange(previousMonthDate);
        return;
      }

      container.scrollBy({
        left: -dayWidth * 7,
        behavior: "smooth",
      });
    } else {
      const { scrollLeft, scrollWidth, clientWidth } = container;
      const currentDate = new Date(
        `${currentMonthYear.month} 1, ${currentMonthYear.year}`,
      );
      const monthIndex = currentDate.getMonth();

      if (monthIndex === 11 && isLastDayOfMonthVisible(container, days)) {
        const nextMonthDate = getAdjacentMonth(currentMonthYear, 1);
        triggerMonthChange(nextMonthDate);
        return;
      }

      if (scrollLeft + clientWidth >= scrollWidth - 1) {
        const nextMonthDate = getAdjacentMonth(currentMonthYear, 1);
        triggerMonthChange(nextMonthDate);
        return;
      }

      // Default scroll
      container.scrollBy({
        left: dayWidth * 7,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !selectedDay) return;

    const firstChild = container.querySelector("div > div");
    if (!firstChild) return;

    const dayWidth = firstChild.getBoundingClientRect().width;
    const index = days.findIndex((d) => isSameDay(d.date, selectedDay.date));
    if (index !== -1) {
      const weekIndex = Math.floor((index + leadingDayCount) / 7);
      container.scrollTo({
        left: weekIndex * dayWidth * 7,
        behavior: "smooth",
      });
    }

    updateScrollButtons();
  }, [selectedDay, days]);

  useEffect(() => {
    if (!shouldScrollToLastWeek.current) return;
    const container = scrollContainerRef.current;
    if (!container || days.length === 0) return;

    // Use a small delay to ensure DOM is ready
    const timeoutId = setTimeout(() => {
      const lastDay = container.lastElementChild as HTMLElement;

      if (lastDay) {
        container.scrollTo({
          left:
            lastDay.offsetLeft - container.clientWidth + lastDay.offsetWidth,
          behavior: "smooth",
        });
        shouldScrollToLastWeek.current = false;
      }
    }, 50); // small delay, can increase if needed

    return () => clearTimeout(timeoutId);
  }, [days]);

  const { label, distanceInDays } = getSelectedDayMeta(selectedDay);

  return (
    <div className={tw("relative", className)}>
      {canScrollLeft && (
        <button
          onClick={() => scrollByWeek(SCROLL_DIRECTION.LEFT)}
          aria-label="Previous week"
          className="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 backdrop-blur-sm hover:bg-white"
        >
          <icons.outline.ChevronLeftIcon className="text-indigo size-5" />
        </button>
      )}
      {canScrollRight && (
        <button
          onClick={() => scrollByWeek(SCROLL_DIRECTION.RIGHT)}
          aria-label="Next week"
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 backdrop-blur-sm hover:bg-white"
        >
          <icons.outline.ChevronRightIcon className="text-indigo size-5" />
        </button>
      )}

      <div className="border-y px-7 py-3">
        <div
          ref={scrollContainerRef}
          onScroll={checkMonthScroll}
          className={tw(
            "scrollbar-hide relative flex snap-x snap-mandatory overflow-x-auto",
            "touch-pan-x",
          )}
        >
          {[...leadingDays, ...days, ...trailingDays].map((day, index) => {
            const isPaddingDay =
              index < leadingDayCount || index >= leadingDayCount + days.length;
            const isSelectable = !isPaddingDay && hasAppointments(day.date);

            return (
              <div
                key={day.date.toISOString()}
                data-date={isPaddingDay ? undefined : day.date.toISOString()}
                aria-hidden={isPaddingDay || undefined}
                className={tw(
                  "w-1/7 flex shrink-0 flex-col items-center gap-1 px-0.5",
                  index % 7 === 0 && "snap-start",
                )}
              >
                <span
                  aria-hidden="true"
                  className="text-xs font-medium text-neutral-800"
                >
                  {day.dayOfWeek}
                </span>
                <button
                  onClick={() => {
                    if (isSelectable) onDaySelect(day);
                  }}
                  disabled={!isSelectable}
                  aria-label={getCalendarDayAriaLabel(day.date, {
                    isSelected: isSameDay(selectedDay.date, day.date),
                    isUnavailable: !isSelectable,
                  })}
                  className={tw(
                    "flex aspect-square w-full max-w-12 flex-col items-center justify-center rounded-full transition-all",
                    isSameDay(selectedDay.date, day.date) &&
                      "bg-violet text-white",
                    !isSameDay(selectedDay.date, day.date) &&
                      isSelectable &&
                      "bg-soft-purple text-indigo",
                    !isSameDay(selectedDay.date, day.date) &&
                      !isSelectable &&
                      "cursor-not-allowed text-gray-700 opacity-60",
                  )}
                >
                  <span className="shrink-0 text-sm font-medium">
                    {day.dayNumber}
                  </span>
                  {day.isToday && (
                    <div
                      className={tw(
                        "size-1 rounded-full",
                        isSameDay(selectedDay.date, day.date)
                          ? "bg-soft-purple"
                          : "bg-violet",
                      )}
                    />
                  )}
                </button>
              </div>
            );
          })}
        </div>
      </div>
      {!hideSelectedDay && (
        <div className="mt-3 text-center">
          <div
            translate="no"
            className="notranslate text-midnight-indigo font-sans"
          >
            <p className="text-base font-medium">{label}</p>

            {distanceInDays && (
              <p className="text-sm md:text-xs">({distanceInDays})</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
